import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Tooltip, makeStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DescriptionIcon from '@material-ui/icons/Description';
import PersonIcon from '@material-ui/icons/Person';
import cx from 'classnames';
import {
  LOGIN_URL,
  TEACHERS_HOME_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { isNil } from 'lodash';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { getLiveClassroomInsightsExportURL, logout } from 'sdk';

import { ReactComponent as ExportIcon } from 'pages/Teachers/ClassroomInsights/assets/export-icon.svg';
import { ReactComponent as PaperIcon } from 'pages/Teachers/ClassroomInsights/assets/paper-icon.svg';
import { ReactComponent as ReuploadIcon } from 'pages/Teachers/ClassroomInsights/assets/reupload-icon.svg';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import AccountMenuButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/AccountMenuButton';
import HelpButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/HelpButton';
import { colors } from 'theme/palette';
import { shutdownIntercomWidget } from 'utils/integrations/intercom';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { default as pusherManager } from 'utils/integrations/pusher';
import { openInNewTab } from 'utils/urls';
import { reverse } from 'utils/urls';

import Button from 'components/Button';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const HOME_PAGE_ENABLED = process.env.REACT_APP_HOME_PAGE_ENABLED === 'true';

const tooltipUseStyles = makeStyles({
  customTooltip: {
    backgroundColor: '#333867',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'Nunito'
  }
});

const Header = ({
  data,
  dataIsLoading,
  aboveCount,
  atCount,
  belowCount,
  needsReviewCount,
  loadingCount,
  onPlusButtonClick
}) => {
  const history = useHistory();

  const { trackerInstanceSectionId } = useParams();
  const user = useContext(UserContext);
  const { navigation } = useNavbar({ title: '' });

  const tooltipStyles = tooltipUseStyles();

  const logoutAndRedirect = async () => {
    shutdownIntercomWidget();
    pusherManager.disconnect();
    await logout().then(() => {
      history.push(LOGIN_URL);

      if (process.env.REACT_APP_MIXPANEL_KEY) {
        mixpanel.reset();
      }
    });
  };

  useEffect(() => {
    if (isNil(data)) return;

    if (!data.is_math_assignment) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [data, history]);

  const handleBackButtonClick = useCallback(() => {
    if (HOME_PAGE_ENABLED) {
      history.push(TEACHERS_HOME_URL);
    } else {
      history.push(TEACHER_ASSIGNMENTS_V2_URL);
    }
  }, [history]);

  const submitted =
    aboveCount + atCount + belowCount + needsReviewCount + loadingCount;

  const handleSwitchButtonClick = useCallback(() => {
    navigation?.setNoTopNavigation(false);
    history.push({
      pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
        trackerInstanceSectionId
      })
    });

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'switch to old insights experience',
        class_id: data?.section_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [
    history,
    navigation,
    trackerInstanceSectionId,
    user.id,
    data?.section_id
  ]);

  const exportResults = useCallback(() => {
    openInNewTab(
      getLiveClassroomInsightsExportURL({ trackerInstanceSectionId })
    );
    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'export data',
        class_id: data.section_id,
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [trackerInstanceSectionId, user.id, data?.section_id]);

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.leftPart}>
          <IconButton onClick={handleBackButtonClick}>
            <ArrowBackIcon />
          </IconButton>
          <div className={styles.header}>
            {dataIsLoading && <CircularProgress size={20} />}
            {!dataIsLoading && (
              <>
                <Typography
                  variant="H-TEXT-1"
                  color={colors.blue1}
                  style={{ textTransform: 'none' }}
                >
                  {data?.tracker_name}
                </Typography>

                <div className={styles.meta}>
                  <div>{data?.section_name}</div>
                  <div>
                    <DescriptionIcon />
                    {data?.pages_count} Pages
                  </div>
                  <div>
                    <DateRangeIcon />
                    {data?.due_at && moment(data?.due_at).format('MMM D, YYYY')}
                  </div>
                  <div>
                    <PersonIcon />
                    {data?.students_count} students
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.rightPart}>
          <Button
            endIcon={<ArrowRightAltIcon color="primary" />}
            fontSize="large"
            variant="primary"
            onClick={handleSwitchButtonClick}
            className={styles.switchButton}
          >
            Switch to old Insights Experience
          </Button>
          <div className={styles.actionsWrapper}>
            <HelpButton user={user?.user} />
            <AccountMenuButton user={user?.user} logout={logoutAndRedirect} />
          </div>
        </div>
      </div>
      <div className={styles.statuses}>
        <div className={styles.statusBox}>
          <b>{aboveCount}</b>{' '}
          <Typography variant="S-TEXT-1">Celebrate</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.yellowBox)}>
          <b>{atCount}</b> <Typography variant="S-TEXT-1">Support</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.redBox)}>
          <b>{belowCount}</b>
          <Typography variant="S-TEXT-1">Intervene</Typography>
        </div>
        <Tooltip
          title={
            needsReviewCount >= 1
              ? 'AI was unable to fully process these students work due to missing, blurry, or unreadable content. Teacher review is needed to confirm mastery.'
              : ''
          }
          classes={{ tooltip: tooltipStyles.customTooltip }}
        >
          <div className={styles.reviewState}>
            <ReuploadIcon />
            <Typography variant="S-TEXT-1">
              <b>({needsReviewCount})</b> Reupload Needed
            </Typography>
          </div>
        </Tooltip>

        <div className={styles.studentUploads}>
          <PaperIcon />
          <Typography variant="S-TEXT-1">
            {submitted}/{data?.students_count} Uploads
          </Typography>
        </div>
        <div className={styles.buttons}>
          <Tooltip
            title="Upload More Work"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton
              className={styles.addButton}
              onClick={onPlusButtonClick}
            >
              <AddIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Export Score"
            classes={{ tooltip: tooltipStyles.customTooltip }}
          >
            <IconButton onClick={exportResults} styles={{ padding: '0' }}>
              <ExportIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Header;
